// src/components/patient/OnboardingModal.tsx
import React, { useState, useEffect } from "react";
import { Modal, Form, Input, DatePicker, Select, Steps, Button, Typography, Divider, Row, Col } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, CheckCircleOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { User } from "../../../types/admin/UserTypes";
import { updateProfile } from "../../../api/admin/userApi";
import { useNotifications } from "../../../context/NotificationContext";
import { LOCKED_NAME_INPUT_CLASS } from "../../healthcare/patient/patientNames/patientNameDisplay";
import { withoutPrimaryName } from "../../../utils/profile/selfProfileName";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface OnboardingModalProps {
	visible: boolean;
	user: User;
	setUser: React.Dispatch<React.SetStateAction<User>>;
	onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ visible, user, setUser, onComplete }) => {
	const [form] = Form.useForm();
	const { showSuccess, showError } = useNotifications();
	const [currentStep, setCurrentStep] = useState(0);
	const [loading, setLoading] = useState(false);

	const updateUserField = (field: keyof User, value: any) => {
		setUser(prevUser => ({
			...prevUser,
			[field]: value
		}));
	};

	useEffect(() => {
		if (visible && user) {
			form.setFieldsValue({
				email: user.email,
				phone: user.phone,
				date_of_birth: user.date_of_birth ? dayjs(user.date_of_birth) : null,
				gender: user.gender,
				address: user.address,
				city: user.city,
				state: user.state,
				postal_code: user.postal_code,
				country: user.country,
			});
		}
	}, [visible, user, form]);

	const handleNext = async () => {
		try {
			if (currentStep === 0) {
				await form.validateFields(["email", "phone"]);
				setCurrentStep(1);
			} else if (currentStep === 1) {
				await form.validateFields(["date_of_birth", "gender"]);
				setCurrentStep(2);
			} else if (currentStep === 2) {
				await form.validateFields(["address", "city", "state", "postal_code", "country"]);
				handleFinish();
			}
		} catch (error) {
			console.error("Validation error:", error);
		}
	};

	const handleBack = () => {
		setCurrentStep(currentStep - 1);
	};

	const handleFinish = async () => {
		try {
			setLoading(true);
			const values = await form.validateFields();

			const updatedUser: User = {
				...user,
				...values,
				date_of_birth: values.date_of_birth ? values.date_of_birth.toDate() : null,
				xoe_clinical_onboarding: true,
			};

			// CU-86d439zzf: `...user` would re-send the stored name even though the
			// fields are read-only, so the keys are stripped from the payload itself.
			await updateProfile(withoutPrimaryName(updatedUser) as User);
			showSuccess("Profile updated successfully!");
			onComplete();
		} catch (error) {
			console.error("Error updating profile:", error);
			showError("Failed to update profile");
		} finally {
			setLoading(false);
		}
	};

	const steps = [
		{
			title: "Personal",
			icon: <UserOutlined />,
		},
		{
			title: "Details",
			icon: <UserOutlined />,
		},
		{
			title: "Address",
			icon: <HomeOutlined />,
		},
	];

	return (
		<Modal
			open={visible}
			closable={false}
			maskClosable={false}
			destroyOnClose
			width={600}
			footer={null}
			styles={{
				body: { padding: "24px" },
			}}
		>
			<div className="text-center mb-6">
				<Title level={3} className="mb-2">
					Welcome to Your Health Portal
				</Title>
				<Text type="secondary" className="text-sm">
					Let's set up your profile to get started
				</Text>
			</div>

			<Steps current={currentStep} items={steps} size="small" className="mb-6" />

			<Form form={form} layout="vertical" size="small">
				{currentStep === 0 && (
					<div className="animate-fadeIn">
						<Paragraph type="secondary" className="text-xs mb-4">
							Please verify your basic information
						</Paragraph>
						<Row gutter={12}>
							{/*
							 * CU-86d439zzf: onboarding confirms the recorded name, it does not
							 * change it. Both fields stay visible and read-only, carry no
							 * validation rule, and are omitted from the update payload.
							 */}
							<Col span={12}>
						<Form.Item label="First Name">
							<Input aria-label="First Name" className={LOCKED_NAME_INPUT_CLASS} disabled value={user.first_name || ""} prefix={<UserOutlined />} placeholder="First Name" />
						</Form.Item>
							</Col>
							<Col span={12}>
						<Form.Item label="Last Name">
							<Input aria-label="Last Name" className={LOCKED_NAME_INPUT_CLASS} disabled value={user.last_name || ""} prefix={<UserOutlined />} placeholder="Last Name" />
						</Form.Item>
							</Col>
						</Row>
						<Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Valid email required" }]}>
							<Input prefix={<MailOutlined />} placeholder="Email Address" onChange={(e) => updateUserField('email', e.target.value)} />
						</Form.Item>
						<Form.Item name="phone" label="Phone" rules={[{ required: true, message: "Required" }]}>
							<Input prefix={<PhoneOutlined />} placeholder="Phone Number" onChange={(e) => updateUserField('phone', e.target.value)} />
						</Form.Item>
					</div>
				)}

				{currentStep === 1 && (
					<div className="animate-fadeIn">
						<Paragraph type="secondary" className="text-xs mb-4">
							Help us personalize your experience
						</Paragraph>
				<Form.Item name="date_of_birth" label="Date of Birth" rules={[{ required: true, message: "Required" }]}>
					<DatePicker className="w-full" format="DD/MM/YYYY" placeholder="Select Date" onChange={(value) => updateUserField('date_of_birth', value ? value.toDate() : null)} />
				</Form.Item>
						<Form.Item name="gender" label="Gender" rules={[{ required: true, message: "Required" }]}>
							<Select 
								placeholder="Select Gender" 
								onChange={(value) => updateUserField('gender', value)}
							>
								<Option value="Male">Male</Option>
								<Option value="Female">Female</Option>
								<Option value="Other">Other</Option>
							</Select>
						</Form.Item>
					</div>
				)}

				{currentStep === 2 && (
					<div className="animate-fadeIn">
						<Paragraph type="secondary" className="text-xs mb-4">
							Complete your contact information
						</Paragraph>
						<Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
							<Input prefix={<HomeOutlined />} placeholder="Street Address" onChange={(e) => updateUserField('address', e.target.value)} />
						</Form.Item>
						<Row gutter={12}>
							<Col span={12}>
								<Form.Item name="city" label="City" rules={[{ required: true, message: "Required" }]}>
									<Input placeholder="City" onChange={(e) => updateUserField('city', e.target.value)} />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item name="state" label="State" rules={[{ required: true, message: "Required" }]}>
									<Input placeholder="State" onChange={(e) => updateUserField('state', e.target.value)} />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={12}>
							<Col span={12}>
								<Form.Item name="postal_code" label="Postal Code" rules={[{ required: true, message: "Required" }]}>
									<Input placeholder="Postal Code" onChange={(e) => updateUserField('postal_code', e.target.value)} />
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item name="country" label="Country" rules={[{ required: true, message: "Required" }]}>
									<Input placeholder="Country" onChange={(e) => updateUserField('country', e.target.value)} />
								</Form.Item>
							</Col>
						</Row>
					</div>
				)}
			</Form>

			<Divider className="my-4" />

			<div className="flex justify-between items-center">
				<Button size="small" disabled={currentStep === 0} onClick={handleBack}>
					Back
				</Button>
				<div className="flex gap-1">
					{steps.map((_, index) => (
						<div key={index} className={`h-1 w-8 rounded-full transition-all ${index <= currentStep ? "bg-blue-500" : "bg-gray-200"}`} />
					))}
				</div>
				<Button type="primary" size="small" onClick={handleNext} loading={loading} icon={currentStep === 2 ? <CheckCircleOutlined /> : <RightOutlined />}>
					{currentStep === 2 ? "Complete" : "Next"}
				</Button>
			</div>
		</Modal>
	);
};

export default OnboardingModal;
